import './index.less';

import { Button, Form, InputNumber, message, Modal } from 'antd';
import React, { useState } from 'react';
import { useDispatch } from 'react-redux';

import { AppDispatch } from '../../../store/store';
import { updateRefinanceRefinanceFeeThunk } from '../../../services/refinanceService/refinanceThunk';
import { Refinance } from '../../../features/refinance/refinanceSlice';
import { onCalculateStampingDutyFee } from '../../../utils/calculation.util';
import { roundTo } from '../../../helpers/number';

type UpdateRefinancingFeesModalProps = {
  refinance: Refinance;
  callback?: () => void;
};

const UpdateRefinancingFeesModal: React.FC<UpdateRefinancingFeesModalProps> = (props) => {
  const { refinance, callback } = props;
  const dispatch = useDispatch<AppDispatch>();

  const [form] = Form.useForm();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const minRefinanceFee = () => {
    const stampingDutyFee = onCalculateStampingDutyFee(
      refinance.application?.financeAmount ?? 0,
      refinance.application?.productStampingDutyPercentage ?? 0
    );
    return Number(roundTo(stampingDutyFee + (refinance.application.loanTotalPremium ?? 0), 2));
  };

  const showModal = () => {
    setIsModalOpen(true);
  };

  const handleCancel = () => {
    setIsModalOpen(false);
    setLoading(false);
    form.resetFields();
  };

  const onFinish = async () => {
    await form
      .validateFields()
      .then(async (values) => {
        setLoading(true);

        await dispatch(
          updateRefinanceRefinanceFeeThunk({
            id: refinance?.id,
            refinanceFee: values.refinanceFee,
          })
        )
          .unwrap()
          .then(() => {
            if (callback) callback();
            message.success('Refinancing Fee Updated Successfully');
          });
      })
      .catch((err) => {
        message.error(
          err.errorFields?.[0]?.errors?.[0] ?? 'Something went wrong. Please try again later.'
        );
      })
      .finally(() => setLoading(false));
  };

  return (
    <>
      <Button type="primary" onClick={showModal} ghost>
        Update
      </Button>
      <Modal
        title="Update Refinancing Fee"
        okText="Confirm"
        open={isModalOpen}
        onOk={onFinish}
        onCancel={handleCancel}
        confirmLoading={loading}
        width={360}
        destroyOnClose
        centered
      >
        <Form
          form={form}
          layout="vertical"
          autoComplete="off"
          requiredMark={false}
          initialValues={{
            refinanceFee: refinance.refinanceFee,
          }}
        >
          <Form.Item
            name="refinanceFee"
            rules={[
              { required: true, message: 'Refinance fee is required' },
              {
                type: 'number',
                min: minRefinanceFee(),
                message: `Refinance fee must be at least RM ${minRefinanceFee()}`,
              },
            ]}
          >
            <InputNumber
              placeholder="Please input"
              style={{ width: '100%' }}
              controls={false}
              inputMode="numeric"
              type={'number'}
            />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default UpdateRefinancingFeesModal;
